<template>
  <div id="deductions-modal-app">
    <modal
      elem-id="modal_crud_deduction"
      size="formOptimized"
      :close-label="t('Fechar')"
      :save-label="t('Adicionar Dedução')"
      :save-disabled-fn="() => !canSave || isLoading"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
      <deductions-form
        v-if="modalData.periodId && modalData.userId"
        ref="formRef"
        :period-id="modalData.periodId"
        :user-id="modalData.userId"
        :user-name="modalData.userName ?? ''"
        :franchise-id="modalData.franchiseId ?? 0"
        :hide-actions="true"
        @saved="handleSaved"
        @close="closeModal"
      />
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import DeductionsForm from './DeductionsForm.vue';

interface ModalData {
  periodId: number | null;
  userId: string | null;
  userName: string | null;
  franchiseId: number | null;
}

interface DeductionsFormInstance {
  handleSubmit: () => Promise<void>;
  canSave: boolean;
  resetForm: () => void;
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const formRef = ref<DeductionsFormInstance | null>(null);
const isLoading = ref<boolean>(false);
const modalData = ref<ModalData>({
  periodId: null,
  userId: null,
  userName: null,
  franchiseId: null
});

const modalTitle = computed<string>(() => {
  return t('Adicionar Dedução');
});

const canSave = computed<boolean>(() => {
  return formRef.value?.canSave ?? false;
});

const openModal = async (data: ModalData): Promise<void> => {
  log('DeductionsModal', 'openModal', 'Opening modal', data);

  if (isVisible.value) {
    log('DeductionsModal', 'openModal', 'Modal already visible, ignoring event');
    return;
  }

  modalData.value = {
    periodId: data.periodId,
    userId: data.userId,
    userName: data.userName,
    franchiseId: data.franchiseId
  };

  isVisible.value = true;

  await nextTick();

  if (formRef.value?.resetForm) {
    formRef.value.resetForm();
  }
};

const closeModal = (): void => {
  log('DeductionsModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  modalData.value = {
    periodId: null,
    userId: null,
    userName: null,
    franchiseId: null
  };
};

const handleSave = async (): Promise<void> => {
  log('DeductionsModal', 'handleSave', 'Save triggered from modal footer');

  if (!formRef.value || !formRef.value.handleSubmit) {
    log('DeductionsModal', 'handleSave', 'Form ref or handleSubmit not available');
    return;
  }

  if (!canSave.value) {
    log('DeductionsModal', 'handleSave', 'Form is not valid');
    return;
  }

  isLoading.value = true;
  try {
    await formRef.value.handleSubmit();
  } finally {
    isLoading.value = false;
  }
};

const handleSaved = (): void => {
  log('DeductionsModal', 'handleSaved', 'Deduction saved successfully');
  eventBus.emit('deduction.saved');
  closeModal();
};

const handleOpenModal = (data?: ModalData): void => {
  if (!data) return;
  void openModal(data);
};

onMounted(() => {
  log('DeductionsModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('deduction.open-modal', handleOpenModal);
});

onBeforeUnmount(() => {
  log('DeductionsModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('deduction.open-modal', handleOpenModal);
});

defineExpose({
  openModal,
  closeModal
});
</script>

<style scoped>
#deductions-modal-app {
  /* Modal wrapper styles if needed */
}
</style>

